import { useState } from 'react'
import { DocumentNode } from 'graphql'
import { InfoCircleOutlined, UploadOutlined } from '@ant-design/icons'
import { Alert, Collapse } from 'antd'
import PartDFMReportCard from '@Admin/AdminQuotingPage/components/PartDFMReportPanel/PartDFMReportCard'
import UploadDFMReportButton from '@Admin/AdminQuotingPage/components/PartDFMReportPanel/UploadDFMReportButton'
import { useGetOboDetails } from '@Admin/AdminQuotingPage/components/PartDFMReportPanel/logic'
import { getRevisionHistoryUrl } from '@Builder/QuoteDetailPage/FilesTab/logic'

import Box from '~/core/Box'
import Link from '~/core/Link'
import Text from '~/core/Text'
import Button from '~/core/Button'
import getColor from '~/lib/getColor'
import UploadQuoteModelRevisionModal from '~/apps/consumer/components/UploadQuoteModelRevisionModal'
import PartDFMReportFeedbackHelp from '../PartDFMReportFeedbackHelp'
import { PART_DFM_REPORT } from '../constants'
import {
    quoteModelRevisionWithHoles_quoteModelRevision as quoteModelRevisionType,
    quoteModelRevisionWithHoles_quoteModelRevision_dfmReports as dfmReportsType,
} from '../__generated__/quoteModelRevisionWithHoles'
import {
    QuoteDetails_Quote_QuoteModels_Item_SelectedQuoteModelRevisionFragment,
    AdminQuoteModelRevisionDataFragment,
} from '~/gql/graphql'
import {
    coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision,
    coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision_dfmReports,
} from '~/redux-modules/query-refactor/__generated__/coreQuoteDetails'

import styles from './styles.css'

type PartDFMReportFeedbackCardPropsType = {
    quoteId: string
    qmrId?: string
    quoteUserId?: number
    quoteModelRevision?:
        | quoteModelRevisionType
        | AdminQuoteModelRevisionDataFragment
        | QuoteDetails_Quote_QuoteModels_Item_SelectedQuoteModelRevisionFragment
        | coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision
        | null
    userId?: number
    isImQuote?: boolean
    isQuoteLocked?: boolean
    isLocatedInAdminPanel?: boolean
    isManufacturabilityFeedbackTab?: boolean
    isPartViewerModal?: boolean
    refetchQuery: RefetchQueryType[]
    dfmReports:
        | dfmReportsType[]
        | coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision_dfmReports[]
    qmrVersion?: number
    manualRefetch?: (id: string) => void
}

type RefetchQueryType = { query: DocumentNode; variables: { id: string } }

export default function PartDFMReportFeedbackCard(
    props: PartDFMReportFeedbackCardPropsType,
) {
    const {
        quoteId,
        qmrId,
        quoteModelRevision,
        quoteUserId,
        userId,
        isImQuote,
        isQuoteLocked,
        isLocatedInAdminPanel,
        isManufacturabilityFeedbackTab,
        isPartViewerModal,
        refetchQuery,
        dfmReports,
        qmrVersion,
        manualRefetch,
    } = props

    const [isCollapse, setCollapse] = useState(true)
    const [showUploadRevisionModal, setShowUploadRevisionModal] =
        useState(false)

    const { isObo, oboUserId } = useGetOboDetails(quoteUserId || '')

    const publishedDfmReports = dfmReports?.filter(report => report.isPublished)
    const latestDfmReport = publishedDfmReports?.slice(0, 1)[0] || []
    const earlierDfmReports = publishedDfmReports?.slice(1) || []
    const checkQmrVersionIsNotOne = qmrVersion !== 1

    const onOpenPanel = () => {
        setCollapse(!isCollapse)
    }

    const revisionHistoryUrl = getRevisionHistoryUrl({
        isObo,
        isLocatedInAdminPanel,
        oboUserId,
        qmrId,
        quoteId,
    })

    const PartDfmFeedbackHelp = () => {
        if (checkQmrVersionIsNotOne && publishedDfmReports.length === 0) {
            return null
        }

        return (
            <Box marginTop="2px">
                <Text color="blackAlpha45" fontWeight={400}>
                    No earlier versions of DFM reports for this revision
                </Text>
                {isManufacturabilityFeedbackTab && (
                    <PartDFMReportFeedbackHelp
                        userId={userId}
                        qmrId={qmrId}
                        isLocatedInAdminPanel={isLocatedInAdminPanel}
                    />
                )}
            </Box>
        )
    }

    const PartDFMReportFeedbackHeader = () => (
        <Box justifyContent="start">
            <Text fontSize="14px">
                {isCollapse ? 'Show earlier versions' : 'Hide earlier versions'}
            </Text>
            {isManufacturabilityFeedbackTab && (
                <PartDFMReportFeedbackHelp
                    qmrId={qmrId}
                    userId={userId}
                    isLocatedInAdminPanel={isLocatedInAdminPanel}
                />
            )}
        </Box>
    )

    const PartRevisionDfmReportAlert = () => {
        return (
            <Alert
                message={
                    <>
                        <Box>
                            <InfoCircleOutlined
                                style={{
                                    color: getColor('link'),
                                    alignSelf: 'center',
                                }}
                            />
                            <Text
                                fontSize="16px"
                                style={{
                                    fontWeight: '400',
                                    marginLeft: '20px',
                                }}
                            >
                                Older DFM reports available
                            </Text>
                        </Box>
                        <Box marginLeft="34px">
                            <Text>
                                Please find the DFM reports associated with
                                older part revisions in{' '}
                                <Link
                                    to={revisionHistoryUrl.url}
                                    from={{
                                        backUrlFor: revisionHistoryUrl.backUrl,
                                        userId,
                                    }}
                                    name={`${PART_DFM_REPORT}-revision-history`}
                                    data-test-target={`${PART_DFM_REPORT}-revisions-history-link`}
                                    placement={`${PART_DFM_REPORT}`}
                                >
                                    Revision history
                                </Link>{' '}
                                page.
                            </Text>
                        </Box>
                    </>
                }
                style={{ margin: '2px', border: 'none' }}
            />
        )
    }

    return (
        <Box
            direction="col"
            height="100%"
            style={{
                overflow: 'auto',
                padding: isPartViewerModal ? '0' : '16px',
            }}
        >
            <Box marginBottom="2px">
                <Text color="grey8" fontWeight={400} fontSize="16px">
                    Download and review DFM reports
                </Text>
            </Box>
            <Box marginTop="2px">
                <Text color="grey8" fontWeight={300}>
                    Follow the instructions in the report to approve or ask
                    questions. Then return to this page and upload your DFM
                    report.{' '}
                </Text>
            </Box>
            <Box marginTop="12px">
                {publishedDfmReports?.length > 1 && (
                    <Text color="grey8" fontWeight={400}>
                        Latest version
                    </Text>
                )}
            </Box>
            {publishedDfmReports?.length > 0 && (
                <Box marginTop="3px" direction="col">
                    <PartDFMReportCard
                        key={latestDfmReport?.asset?.id}
                        quoteId={quoteId}
                        dfmReport={latestDfmReport}
                        quoteUserId={quoteUserId}
                        qmrId={qmrId}
                        refetchQuery={refetchQuery}
                        isAdminQuotingPage={false}
                        manualRefetch={manualRefetch}
                    />
                </Box>
            )}
            {publishedDfmReports?.length <= 1 ? (
                <PartDfmFeedbackHelp />
            ) : (
                <Collapse
                    bordered={false}
                    onChange={onOpenPanel}
                    className={styles.cardBox}
                >
                    <Collapse.Panel
                        header={<PartDFMReportFeedbackHeader />}
                        key="1"
                        style={{ border: 'none' }}
                    >
                        <Box direction="col">
                            {earlierDfmReports?.map(dfmReport => (
                                <PartDFMReportCard
                                    key={dfmReport?.asset?.id}
                                    quoteId={quoteId}
                                    dfmReport={dfmReport}
                                    quoteUserId={quoteUserId}
                                    qmrId={qmrId}
                                    refetchQuery={refetchQuery}
                                    isAdminQuotingPage={false}
                                    setCollapse={setCollapse}
                                    earlierDfmReportsLength={
                                        earlierDfmReports?.length
                                    }
                                    manualRefetch={manualRefetch}
                                />
                            ))}
                        </Box>
                    </Collapse.Panel>
                </Collapse>
            )}
            {checkQmrVersionIsNotOne && publishedDfmReports?.length === 0 && (
                <PartRevisionDfmReportAlert />
            )}
            {isManufacturabilityFeedbackTab && (
                <>
                    <UploadQuoteModelRevisionModal
                        visible={showUploadRevisionModal}
                        setUploadRevisionModalVisibility={
                            setShowUploadRevisionModal
                        }
                        quoteId={quoteId}
                        isImQuote={isImQuote}
                        quoteModelRevision={quoteModelRevision}
                        refetchQuery={refetchQuery}
                        manualRefetch={manualRefetch}
                    />
                    {checkQmrVersionIsNotOne &&
                    publishedDfmReports?.length === 0 ? null : (
                        <Box
                            direction="col"
                            alignItems="center"
                            marginTop="30px"
                            marginBottom="30px"
                            flex
                        >
                            <Box marginBottom="6px">
                                <Text color="grey8" fontWeight={400}>
                                    Want to approve DFM or ask questions?
                                </Text>
                            </Box>
                            <UploadDFMReportButton
                                quoteId={quoteId}
                                qmrId={qmrId}
                                refetchQuery={refetchQuery}
                                isManufacturabilityFeedbackTab
                                quoteUserId={quoteUserId}
                                manualRefetch={manualRefetch}
                            />
                            <Box marginTop="16px" marginBottom="6px">
                                <Text color="grey8" fontWeight={400}>
                                    Need to update your model?
                                </Text>
                            </Box>
                            <Button
                                style={{ width: '179px' }}
                                name={`${PART_DFM_REPORT}-upload-revision`}
                                data-test-target={`${PART_DFM_REPORT}-upload-revision-btn`}
                                placement={`${PART_DFM_REPORT}`}
                                disabled={isQuoteLocked}
                                icon={<UploadOutlined />}
                                onClick={() => {
                                    setShowUploadRevisionModal(true)
                                }}
                            >
                                Upload part revision
                            </Button>
                        </Box>
                    )}
                </>
            )}
            {isPartViewerModal && (
                <Box
                    direction="col"
                    alignItems="center"
                    marginTop="30px"
                    marginBottom="40px"
                    flex
                >
                    <Box marginBottom="6px">
                        <Text color="grey8" fontWeight={400}>
                            Want to approve DFM or ask questions?
                        </Text>
                    </Box>
                    <UploadDFMReportButton
                        quoteId={quoteId}
                        qmrId={qmrId}
                        refetchQuery={refetchQuery}
                        quoteUserId={quoteUserId}
                        isPartViewerModal={isPartViewerModal}
                        manualRefetch={manualRefetch}
                    />
                </Box>
            )}
        </Box>
    )
}
